/**
 * useSwipeToReply — WhatsApp-style slide-to-reply gesture (V1.9.27).
 *
 * Attach the returned handlers to any message-bubble wrapper. When the
 * user drags horizontally past the threshold (60 px) toward the reply
 * side (right for incoming, left for outgoing) and releases, we call
 * onReply(). During the drag we translateX the wrapper for tactile
 * feedback and surface a reply-arrow badge on the opposite edge.
 *
 * Works on touch (mobile) + mouse (desktop) via Pointer Events.
 * Ignores vertical scrolling (Y drift > X drift means scroll intent).
 *
 * Returns:
 *   handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel }
 *   dx      : current translateX (px)
 *   swiping : true while pointer is down
 */
import React from "react";

const THRESHOLD_PX = 60;
const MAX_DX = 80;
const V_TOLERANCE = 12;

export function useSwipeToReply({ onReply, onForward, isMine, disabled = false }) {
    // V2.0.20 — Manage transform IMPERATIVELY via a ref instead of React
    // state. Previous approaches (setDx(0) + rAF onReply) could still get
    // stranded on iOS Safari when the parent's re-render (from setReplyTo)
    // interrupted the CSS transition mid-animation. Writing to element
    // .style.transform bypasses React reconciliation and cannot be
    // overridden by a re-render (React preserves inline styles it didn't
    // set). Result: bubble ALWAYS snaps back to 0, no matter what the
    // parent does afterwards.
    const wrapRef = React.useRef(null);
    const [swiping, setSwiping] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [swipeSide, setSwipeSide] = React.useState(null);
    const startRef = React.useRef({ x: 0, y: 0, active: false, hCommitted: false });
    const dxRef = React.useRef(0);

    const replyDir = isMine ? -1 : 1;

    // Apply transform directly to the wrapper DOM node. Called on every
    // pointermove and on release. `animate=true` re-enables the CSS
    // transition so the reset glides back; during drag we keep it none
    // for tactile feedback.
    const applyTransform = React.useCallback((px, animate) => {
        const el = wrapRef.current;
        if (!el) return;
        el.style.transition = animate
            ? "transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1)"
            : "none";
        el.style.transform = `translateX(${px}px)`;
    }, []);

    const clear = React.useCallback((animateReset = true) => {
        startRef.current = { x: 0, y: 0, active: false, hCommitted: false };
        dxRef.current = 0;
        setSwiping(false);
        setProgress(0);
        setSwipeSide(null);
        applyTransform(0, animateReset);
    }, [applyTransform]);

    const onPointerDown = (e) => {
        if (disabled) return;
        if (e.pointerType === "mouse" && e.button !== 0) return;
        startRef.current = { x: e.clientX, y: e.clientY, active: true, hCommitted: false };
        dxRef.current = 0;
        setSwiping(true);
        // Kill any leftover transition so drag feels snappy.
        applyTransform(0, false);
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    };

    const onPointerMove = (e) => {
        const s = startRef.current;
        if (!s.active) return;
        const rawDx = e.clientX - s.x;
        const rawDy = e.clientY - s.y;
        if (!s.hCommitted && Math.abs(rawDy) > V_TOLERANCE && Math.abs(rawDy) > Math.abs(rawDx)) {
            clear(true);
            return;
        }
        if (Math.abs(rawDx) < 4) return;
        s.hCommitted = true;
        const canReply = onReply && ((rawDx > 0 && replyDir === 1) || (rawDx < 0 && replyDir === -1));
        const canForward = onForward && ((rawDx > 0 && replyDir === -1) || (rawDx < 0 && replyDir === 1));
        if (!canReply && !canForward) return;
        const clamped = Math.max(-MAX_DX, Math.min(MAX_DX, rawDx));
        dxRef.current = clamped;
        applyTransform(clamped, false);
        setProgress(Math.min(1, Math.abs(clamped) / THRESHOLD_PX));
        setSwipeSide(clamped > 0 ? "right" : "left");
    };

    const finish = () => {
        const finalDx = dxRef.current;
        // Reset drag state (transforms bubble back to 0 imperatively).
        clear(true);
        if (Math.abs(finalDx) < THRESHOLD_PX) return;
        const isReplyDirection = (finalDx > 0 && replyDir === 1) || (finalDx < 0 && replyDir === -1);
        const fire = isReplyDirection ? onReply : onForward;
        if (fire) {
            // Wait one frame so the bubble's return animation is visibly
            // underway before the parent reflows (composer + ReplyPreviewBar).
            if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(() => fire());
            } else {
                setTimeout(() => fire(), 0);
            }
        }
    };

    // Belt-and-braces: if the component unmounts mid-swipe (e.g. virtual
    // list recycles the row), release any lingering pointer capture and
    // reset the ref-held state.
    React.useEffect(() => {
        return () => {
            dxRef.current = 0;
            startRef.current = { x: 0, y: 0, active: false, hCommitted: false };
        };
    }, []);

    const onPointerUp = () => finish();
    const onPointerCancel = () => clear(true);

    return {
        wrapRef,
        handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
        swiping,
        progress,
        swipeSide,
        // Backwards-compat: some callers still read dx from the return.
        // Keep it as 0 since we no longer track it in state — anything
        // reading swipe.dx as a style value should use wrapRef instead.
        dx: 0,
    };
}

/**
 * ReplyQuote — the quoted-message header rendered INSIDE a bubble
 * when it's replying to another message. WhatsApp-style: colored
 * vertical bar, sender name in bold, one-line snippet.
 *
 * Props:
 *   quoted: { sender_name, body, attachment_kind }  (client-side lookup)
 *   isMine: caller-provided bubble ownership for color styling
 *   onJump: optional callback when user taps the quote to jump-scroll
 */
export function ReplyQuote({ quoted, isMine, onJump }) {
    if (!quoted) return null;
    const snippet = quoted.body?.trim()
        || (quoted.attachment_kind === "voice" ? "🎤 Voice note"
        : quoted.attachment_kind === "image" ? "🖼️ Photo"
        : quoted.attachment_kind === "video" ? "🎬 Video"
        : quoted.attachment_kind ? "📎 Attachment"
        : "Message");
    const clipped = snippet.length > 90 ? `${snippet.slice(0, 90)}…` : snippet;
    // V2.0.20 — `truncate` (white-space:nowrap) on the snippet used to
    // stretch the bubble past its `max-w-[85%]` wrapper because the button
    // has no width containment. Force `min-w-0 max-w-full` + overflow:hidden
    // on the button so the snippet ellipsizes cleanly instead of pushing
    // the whole bubble off-screen.
    return (
        <button
            type="button"
            onClick={onJump}
            data-testid="reply-quote"
            className={`w-full min-w-0 max-w-full overflow-hidden text-left mb-1 pl-2 pr-2 py-1 border-l-4 rounded-r-md cursor-pointer transition
                ${isMine
                    ? "bg-emerald-50 border-emerald-500 hover:bg-emerald-100/70"
                    : "bg-cobalt/5 border-cobalt hover:bg-cobalt/10"}`}
        >
            <span className={`block text-[11px] font-bold truncate ${isMine ? "text-emerald-700" : "text-cobalt"}`}>
                {quoted.sender_name || "You"}
            </span>
            <span className="block text-[12px] text-ink/70 truncate">
                {clipped}
            </span>
        </button>
    );
}

/**
 * ReplyPreviewBar — floats above the composer while the user has
 * selected a message to reply to. Cancel × dismisses.
 */
export function ReplyPreviewBar({ quoted, onCancel }) {
    if (!quoted) return null;
    const snippet = quoted.body?.trim()
        || (quoted.attachment_kind === "voice" ? "🎤 Voice note"
        : quoted.attachment_kind ? "📎 Attachment" : "Message");
    return (
        <div
            data-testid="reply-preview-bar"
            className="flex items-start gap-3 px-3 py-2 border-l-4 border-cobalt bg-white/95 shadow-sm rounded-md mb-2"
        >
            <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-cobalt truncate">
                    Replying to {quoted.sender_name || "message"}
                </p>
                <p className="text-[12px] text-ink/70 truncate">
                    {snippet}
                </p>
            </div>
            <button
                type="button"
                onClick={onCancel}
                data-testid="reply-preview-cancel"
                className="w-6 h-6 rounded-full hover:bg-ink/10 flex items-center justify-center shrink-0 text-ink/60"
                aria-label="Cancel reply"
            >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
        </div>
    );
}

/**
 * SwipeReplyIndicator — the reply arrow that appears in the swipe track,
 * on the opposite edge from the direction of motion. Fades in as
 * progress approaches 1.
 */
export function SwipeReplyIndicator({ progress, isMine, side }) {
    if (!progress) return null;
    const opacity = Math.min(1, progress);
    const scale = 0.6 + 0.4 * Math.min(1, progress);
    // Determine action + side based on the swipe direction (side arg).
    // Fallback to legacy behaviour (reply on isMine's home side) when side not provided.
    const effectiveSide = side ?? (isMine ? "right" : "left");
    const replyDir = isMine ? "left" : "right";
    const isReply = effectiveSide === replyDir;
    return (
        <span
            aria-hidden="true"
            data-testid="swipe-reply-indicator"
            className={`absolute top-1/2 -translate-y-1/2 ${effectiveSide === "right" ? "left-2" : "right-2"} w-8 h-8 rounded-full ${isReply ? "bg-cobalt/15" : "bg-amber-100"} flex items-center justify-center pointer-events-none`}
            style={{ opacity, transform: `translateY(-50%) scale(${scale})` }}
            title={isReply ? "Reply" : "Forward"}
        >
            {isReply ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-cobalt">
                    <polyline points="9 17 4 12 9 7" />
                    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
            ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-700">
                    <polyline points="15 17 20 12 15 7" />
                    <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                </svg>
            )}
        </span>
    );
}
